import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, PartyPopper, MessageCircle, Phone, Calendar, Clock, MapPin, Home, Egg, Sparkles } from "lucide-react";
import { getSeminar } from "@/lib/api";
import AnnouncementBar from "@/components/AnnouncementBar";

export default function ThankYou() {
    const [seminar, setSeminar] = useState(null);
    const [params] = useSearchParams();
    const paymentId = params.get("razorpay_payment_id") || params.get("payment_id");
    const orderId = params.get("razorpay_order_id") || params.get("order_id");

    useEffect(() => {
        getSeminar().then(setSeminar).catch(() => {});
        // Confetti-esque body reveal
        window.scrollTo(0, 0);
    }, []);

    const wa = (seminar?.contact_whatsapp || "").replace(/[^\d+]/g, "");

    return (
        <div className="min-h-screen bg-white">
            <AnnouncementBar businessName="Energy Plus Premium Brown Eggs" fee={seminar?.registration_fee || "199"} />

            {/* Simple header */}
            <div className="border-b border-slate-100">
                <div className="container-max py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="w-10 h-10 rounded-xl bg-orange-gradient flex items-center justify-center shadow-orange">
                            <Egg className="w-5 h-5 text-white" />
                        </div>
                        <div className="leading-tight">
                            <div className="font-extrabold text-lg text-navy">Energy Plus</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Premium Brown Eggs</div>
                        </div>
                    </div>
                    <Link
                        to="/"
                        className="text-sm text-slate-600 hover:text-brand-orange font-semibold inline-flex items-center gap-1"
                        data-testid="thankyou-home"
                    >
                        <Home className="w-4 h-4" /> Home
                    </Link>
                </div>
            </div>

            {/* Success hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-dots [background-size:22px_22px] opacity-70 -z-10" />
                <div className="absolute inset-0 -z-10 bg-[radial-gradient(50%_50%_at_50%_20%,rgba(16,185,129,0.10),transparent)]" />
                <div className="container-max py-16 md:py-24 text-center max-w-3xl mx-auto">
                    <motion.div
                        initial={{ scale: 0.4, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 220, damping: 15 }}
                        className="w-28 h-28 rounded-full bg-emerald-100 border-4 border-emerald-200 mx-auto flex items-center justify-center shadow-lift"
                    >
                        <CheckCircle2 className="w-16 h-16 text-emerald-600" strokeWidth={2.5} />
                    </motion.div>

                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                    >
                        <span className="mt-8 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-4 py-1.5 text-xs uppercase tracking-widest font-bold text-emerald-700">
                            <PartyPopper className="w-3.5 h-3.5" /> Payment Successful
                        </span>
                        <h1 className="mt-6 marathi text-4xl sm:text-5xl lg:text-6xl font-black text-navy leading-[1.2]" style={{letterSpacing:'-0.02em'}}>
                            धन्यवाद! <span className="text-brand-orange">तुमची नोंदणी झाली</span>
                        </h1>
                        <p className="mt-5 text-lg text-slate-600 marathi font-medium leading-relaxed">
                            Energy Plus Premium Brown Eggs — Business Opportunity Seminar साठी तुमची जागा राखीव झाली आहे.<br className="hidden md:block" />
                            आमची team लवकरच WhatsApp / Call वर तुमच्याशी संपर्क साधेल.
                        </p>
                    </motion.div>

                    {(paymentId || orderId) && (
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.35, duration: 0.5 }}
                            className="mt-8 inline-flex flex-col items-start gap-1 rounded-2xl bg-white border border-slate-200 shadow-soft px-6 py-4 text-left"
                        >
                            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Payment Reference</div>
                            {paymentId && <div className="font-mono text-sm text-navy">Payment ID: {paymentId}</div>}
                            {orderId && <div className="font-mono text-sm text-navy">Order ID: {orderId}</div>}
                        </motion.div>
                    )}
                </div>
            </section>

            {/* Seminar details recap */}
            {seminar && (
                <section className="pb-16">
                    <div className="container-max max-w-4xl">
                        <motion.div
                            initial={{ y: 30, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="rounded-3xl bg-white border border-slate-200 shadow-lift p-8 md:p-10"
                        >
                            <div className="flex items-center gap-2 mb-6">
                                <Sparkles className="w-5 h-5 text-brand-orange" />
                                <h2 className="text-xl font-extrabold text-navy tracking-tight">Seminar Details — पुन्हा एकदा</h2>
                            </div>
                            <div className="grid md:grid-cols-3 gap-6">
                                <DetailTile icon={Calendar} label="Date" value={seminar.date_text} />
                                <DetailTile icon={Clock} label="Time" value={seminar.time_text} />
                                <DetailTile icon={MapPin} label="Venue" value={seminar.venue} />
                            </div>
                            <div className="mt-8 rounded-2xl bg-brand-orangeLight border border-brand-orange/20 p-5 text-center">
                                <p className="marathi text-navy font-semibold">
                                    कृपया ठरलेल्या दिवशी वेळेवर पोहचा. जागा confirmed आहे.
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </section>
            )}

            {/* Next Steps */}
            <section className="pb-20 bg-slate-50">
                <div className="container-max max-w-4xl py-16">
                    <div className="text-center mb-10">
                        <span className="chip"><MessageCircle className="w-3.5 h-3.5" /> Next Steps</span>
                        <h2 className="mt-4 text-3xl md:text-4xl font-extrabold text-navy tracking-tight marathi">
                            पुढील पायऱ्या
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        <StepCard num="01" title="Confirmation" desc="WhatsApp/SMS वर लवकरच confirmation message येईल" />
                        <StepCard num="02" title="Reminder Call" desc="Seminar च्या एक दिवस आधी आमची team call करेल" />
                        <StepCard num="03" title="Attend Seminar" desc="Address + Google Maps link वेळेवर share केला जाईल" />
                    </div>

                    {/* Contact CTAs */}
                    <div className="mt-12 grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                        <a
                            href={`https://wa.me/${wa}?text=${encodeURIComponent("नमस्कार, मी आत्ता Energy Plus Seminar साठी नोंदणी केली आहे.")}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-center gap-3 rounded-2xl bg-emerald-500 text-white font-bold px-6 py-4 shadow-soft hover:shadow-lift hover:bg-emerald-600 transition-all"
                            data-testid="thankyou-whatsapp"
                        >
                            <MessageCircle className="w-5 h-5" />
                            WhatsApp Confirmation
                        </a>
                        <a
                            href={`tel:${seminar?.contact_phone || ""}`}
                            className="flex items-center justify-center gap-3 rounded-2xl bg-navy-gradient text-white font-bold px-6 py-4 shadow-navy hover:shadow-lift transition-all"
                            data-testid="thankyou-call"
                        >
                            <Phone className="w-5 h-5" />
                            Call {seminar?.contact_phone || "+91 7507172181"}
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-navy-gradient text-white/80 py-10">
                <div className="container-max flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-2.5">
                        <div className="w-10 h-10 rounded-xl bg-orange-gradient flex items-center justify-center shadow-orange">
                            <Egg className="w-5 h-5 text-white" />
                        </div>
                        <div className="leading-tight">
                            <div className="font-extrabold text-lg text-white">Energy Plus</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-semibold">Premium Brown Eggs</div>
                        </div>
                    </div>
                    <div className="text-xs text-white/60">© {new Date().getFullYear()} Energy Plus. All rights reserved.</div>
                </div>
            </footer>
        </div>
    );
}

function DetailTile({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-orange-gradient flex items-center justify-center shrink-0 shadow-orange">
                <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</div>
                <div className="text-lg font-bold text-navy mt-1">{value}</div>
            </div>
        </div>
    );
}

function StepCard({ num, title, desc }) {
    return (
        <div className="rounded-2xl bg-white border border-slate-200 shadow-soft hover:shadow-lift transition-all p-6">
            <div className="w-11 h-11 rounded-xl bg-brand-orangeLight border border-brand-orange/20 flex items-center justify-center">
                <span className="text-brand-orange font-extrabold text-sm">{num}</span>
            </div>
            <div className="mt-4 text-lg font-bold text-navy">{title}</div>
            <p className="mt-2 text-sm text-slate-600 font-medium marathi leading-relaxed">{desc}</p>
        </div>
    );
}
